// Library Methods
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

// Components
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";
import ExamCardContainer from "../card-ui/ExamCardContainer/ExamCardContainer";
import ExamCardHeader from "../card-ui/ExamCardHeader/ExamCardHeader";
import ExamCardButton from "../card-ui/ExamCardButton/ExamCardButton";
import ExamCardToggles from "../card-ui/ExamCardToggles/ExamCardToggles";

// Types
import { SelectedExamCardType } from "src/services/exams/types";

// Constants
import { DEFAULT_DEVIATION_AND_CORRESPONDENCE_CARD } from "src/constants/enums";

type DeviationAndCorrespondenceCardType = {
  label: string;
  selectedExamCard: SelectedExamCardType | undefined;
  updateExamCardProperty: (
    propertyName: keyof SelectedExamCardType,
    value: SelectedExamCardType[keyof SelectedExamCardType]
  ) => void;
  onRemoveCard: () => void;
};

const DeviationAndCorrespondenceCard = ({
  label,
  selectedExamCard,
  updateExamCardProperty,
  onRemoveCard,
}: DeviationAndCorrespondenceCardType) => {
  const { t } = useTranslation();

  const examCardInfo =
    selectedExamCard ?? DEFAULT_DEVIATION_AND_CORRESPONDENCE_CARD;

  const {
    objectiveAlignment,
    subjectiveAngle,
    cyclodeviation,
    optionalToggles,
  } = examCardInfo;

  const booleanOptions = useMemo(
    () => [
      { value: true, label: t("On") },
      { value: false, label: t("Off") },
    ],
    [t]
  );

  const textStyle = {
    color: "#FFF",
    fontSize: "12px",
  };

  // Truncates to a single line with an ellipsis instead of wrapping onto a
  // second line -- minWidth: 0 is needed alongside the usual overflow trio
  // since Typography sits as a flex item here (its default min-width: auto
  // would otherwise keep it from shrinking below the text's full width).
  const titleStyle = {
    ...textStyle,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  };

  // A button only needs to wrap to a new line once its own label can't fit
  // -- not at some arbitrary shared width. "ch" scales with character
  // count, so each button's floor tracks its own text; +2ch covers the
  // button's horizontal padding/border.
  const buttonMinWidth = (buttonLabel: string) => `${buttonLabel.length + 2}ch`;

  return (
    <ExamCardContainer>
      <ExamCardHeader isNew label={label} onRemoveCard={onRemoveCard} />
      <Grid
        item
        container
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        gap="8px"
        flexGrow={1}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "8px",
          }}
        >
          {/* Objective Alignment */}
          <Grid item display="flex" flexDirection="column" gap="4px">
            <TitleWithTooltip
              title={t("Objective Alignment")}
              tooltip={t(
                "Measures the angle of deviation and whether it is a phoria or a tropia."
              )}
              titleStyle={titleStyle}
              tooltipStyle={textStyle}
            />

            <Grid container item display="flex" flexWrap="wrap" gap="4px">
              <Grid item xs fontSize="12px">
                <ExamCardButton
                  isSelected
                  updateValue={() => {
                    updateExamCardProperty("objectiveAlignment", true);
                  }}
                  label={t("On")}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Subjective Angle */}
          {objectiveAlignment && (
            <Grid item display="flex" flexDirection="column" gap="4px">
              <TitleWithTooltip
                title={t("Subjective Angle")}
                tooltip={t(
                  "Measures if the patient reports seeing one picture, and compares it against the objective angle to grade retinal correspondence."
                )}
                titleStyle={titleStyle}
                tooltipStyle={textStyle}
              />

              <Grid container item display="flex" flexWrap="wrap" gap="4px">
                {booleanOptions.map((option) => (
                  <Grid
                    item
                    xs
                    fontSize="12px"
                    minWidth={buttonMinWidth(option.label)}
                    key={String(option.value)}
                  >
                    <ExamCardButton
                      isSelected={
                        option.value
                          ? Boolean(subjectiveAngle)
                          : !subjectiveAngle
                      }
                      updateValue={() =>
                        updateExamCardProperty("subjectiveAngle", option.value)
                      }
                      label={option.label}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}

          {/* Cyclodeviation */}
          {objectiveAlignment && (
            <Grid item display="flex" flexDirection="column" gap="4px">
              <TitleWithTooltip
                title={t("Cyclodeviation")}
                tooltip={t(
                  "Measures torsion, how far each eye is rotated about its own line of sight."
                )}
                titleStyle={titleStyle}
                tooltipStyle={textStyle}
              />

              <Grid container item display="flex" flexWrap="wrap" gap="4px">
                {booleanOptions.map((option) => (
                  <Grid
                    item
                    xs
                    fontSize="12px"
                    minWidth={buttonMinWidth(option.label)}
                    key={String(option.value)}
                  >
                    <ExamCardButton
                      isSelected={
                        option.value ? Boolean(cyclodeviation) : !cyclodeviation
                      }
                      updateValue={() =>
                        updateExamCardProperty("cyclodeviation", option.value)
                      }
                      label={option.label}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <Divider color="#5B6D7A" />

          <ExamCardToggles
            optionalToggles={optionalToggles}
            updateExamCardProperty={updateExamCardProperty}
            showToggles={
              DEFAULT_DEVIATION_AND_CORRESPONDENCE_CARD.optionalToggles
            }
          />
        </Box>
      </Grid>
    </ExamCardContainer>
  );
};

export default DeviationAndCorrespondenceCard;
